import { useApolloClient } from '@apollo/client'
import { useFlag } from '@unleash/proxy-client-react'
import axios from 'axios'
import exifr from 'exifr'
import { getAuth } from 'firebase/auth'
import { chunk } from 'lodash'
import { useMemo } from 'react'
import { useMatch, useParams } from 'react-router-dom'
import { v4 } from 'uuid'
import config, { baseDomain } from '../config'
import {
  BucketAlbumFragmentFragmentDoc,
  BucketFragmentFragmentDoc,
  BucketMediaByAlbumIdDocument,
  ImageDocument,
  MediaStatus
} from '../graphql/generated'
import logger from '../utils/logger'
import Semaphore from '../utils/semaphore'
import useGAEvent from '../views/auth/hooks/useGAEvent'
import useAlerts from '../views/buckets/hooks/useAlerts'
import useS3MulitpartUpload from '../views/buckets/hooks/useS3MulitpartUpload'
import useUploadsInProgress from '../views/buckets/hooks/useUploadsInProgress'
import { videoPlaceholder } from '../views/buckets/utils/placeholders'
import { MediaData } from '../views/buckets/utils/sharedTypes'

interface MediaServiceResponse {
  id: string
  originalFilename: string
  fileS3Key: string
  filename: string
  bucketId: string
  status: string
  failureReason?: string
  createdAt: string
  updatedAt: string
  externalId?: string
  userId: string
  description?: string
  title?: string
  uploadUrls: {
    id: string
    chunkSize: number
    urls: string[]
  }
  livePhoto?: {
    filename: string
    fileSize: number
    fileS3Key: string
    uploadUrls: {
      id: string
      chunkSize: number
      urls: string[]
    }
  }
  metadata: {
    dateTaken: string
    contentType: string
    fileSize: number
    width?: number
    height?: number
    originalFilename: string
    thumbnail?: {
      hash: string
    }
  }
}

interface ExifData {
  width?: number
  height?: number
  dateTaken: string
}

interface UploadPart {
  partNumber: number
  eTag: string
}

interface MediaStatusResponse {
  id: string
  status: string
  thumbnailImage?: {
    url: string
    width?: number
    height?: number
    size?: number
    isLandscape?: boolean
  }
  title?: string
  originalFilename?: string
}

function useUploadImplementation(isEnabled: boolean) {
  const client = useApolloClient()
  const { createAlert } = useAlerts()
  const { beginTrackingProgress, updateStatus } = useUploadsInProgress()
  const { trackEvent } = useGAEvent()
  const params = useParams<{ bucketId: string; albumId: string }>()
  const path = useMatch('/bucket/:bucketId/album/:albumId')
  const bucketId = params.bucketId ?? path?.params.bucketId
  const albumId = params.albumId ?? path?.params.albumId
  const userId = getAuth().currentUser?.uid
  const { uploadSingle: oldUploadSingle } = useS3MulitpartUpload()

  const chunkUploadSemaphore = useMemo(() => new Semaphore(5), [])

  const extractExif = async (file: File): Promise<ExifData> => {
    const now = new Date().toISOString()

    const skipExif = ['gif', 'webp'].includes(file.type.split('/')[1] || '')
    if (skipExif) {
      return { dateTaken: now }
    }

    try {
      const exifData = await exifr.parse(file)
      const { ExifImageHeight, ExifImageWidth, CreateDate } = exifData || {}
      const dateTaken = CreateDate ? new Date(CreateDate).toISOString() : now

      if (ExifImageWidth && ExifImageHeight) {
        return { width: ExifImageWidth, height: ExifImageHeight, dateTaken }
      }

      const img = new Image()
      const imageUrl = URL.createObjectURL(file)

      try {
        const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          img.onload = () => {
            resolve({
              width: img.naturalWidth || 1,
              height: img.naturalHeight || 1
            })
          }
          img.onerror = reject
          img.src = imageUrl
        })

        return { ...dimensions, dateTaken }
      } finally {
        URL.revokeObjectURL(imageUrl)
      }
    } catch (error) {
      logger.error('Failed to extract EXIF:', error)
      return { dateTaken: now }
    }
  }

  const pollMediaStatus = async (mediaId: string) => {
    const maxAttempts = 15
    const pollInterval = 2000
    let attempts = 0

    const token = await getAuth().currentUser?.getIdToken()
    if (!token) {
      logger.error('Authentication token not available for polling')
      return
    }

    const executePoll = async () => {
      if (attempts >= maxAttempts) {
        logger.error(`Polling timed out for mediaId: ${mediaId} after ${maxAttempts} attempts.`)
        updateStatus(mediaId, 'ERROR')
        return
      }

      attempts += 1

      try {
        const response = await axios.get<MediaStatusResponse>(`${config.api.mediaService}/${mediaId}`, {
          headers: {
            Authorization: `${token}`,
            'x-client-name': 'web',
            'x-client-version': process.env.REACT_APP_VERSION || '1.0.0'
          }
        })

        const { status, thumbnailImage } = response.data

        if (status === 'ACTIVE') {
          logger.info(`Status became ACTIVE for mediaId: ${mediaId} after ${attempts} attempts.`)
          updateStatus(mediaId, 'COMPLETE')

          if (thumbnailImage?.url) {
            logger.info(`Thumbnail URL found for mediaId: ${mediaId}: ${thumbnailImage.url}`)

            client.writeQuery({
              query: ImageDocument,
              variables: { id: mediaId },
              data: {
                image: {
                  __typename: 'Image',
                  id: mediaId,
                  thumbnailImage: {
                    __typename: 'ImageFile',
                    url: thumbnailImage.url,
                    width: thumbnailImage.width || 0,
                    height: thumbnailImage.height || 0,
                    size: thumbnailImage.size || 0,
                    isLandscape: thumbnailImage.isLandscape || false
                  },
                  title: response.data.title || '',
                  uploadDate: '',
                  isVideoType: false,
                  username: '',
                  isBlurred: false,
                  status: [status as MediaStatus],
                  albumId: albumId || '',
                  userTags: [],
                  clarifaiTags: [],
                  originalFilename: response.data.originalFilename || '',
                  isMobileUpload: false,
                  albumName: '',
                  image: {
                    __typename: 'ImageFile',
                    width: 0,
                    size: 0,
                    height: 0,
                    url: '',
                    isLandscape: false
                  },
                  originalImage: {
                    __typename: 'ImageFile',
                    width: 0,
                    size: 0,
                    height: 0,
                    url: '',
                    isLandscape: false
                  }
                }
              }
            })
          }

          return
        }

        if (status === 'FAILED') {
          logger.error(`Status became FAILED for mediaId: ${mediaId} on attempt ${attempts}.`)
          updateStatus(mediaId, 'ERROR')
          return
        }

        setTimeout(executePoll, pollInterval)
      } catch (error) {
        logger.error(`Polling error for mediaId ${mediaId} on attempt ${attempts}:`, error)

        if (axios.isAxiosError(error) && error.response?.status === 404) {
          logger.warn(`Media ID ${mediaId} not found yet. This may be normal during processing.`)
        }

        setTimeout(executePoll, pollInterval)
      }
    }

    setTimeout(executePoll, pollInterval)
  }

  const uploadFile = async (file: File) => {
    if (!file || !bucketId || !userId) {
      throw new Error('Missing required upload parameters')
    }

    const mediaId = v4()
    let mediaType = file.type
    const originalFilename = file.name
    const fileSize = file.size
    const fileExt = originalFilename.split('.').pop()?.toLowerCase()

    if (!mediaType && fileExt) {
      logger.warn(`File type empty for ${originalFilename}. Attempting fallback based on extension: ${fileExt}`)
      switch (fileExt) {
        case 'heic':
          mediaType = 'image/heic'
          break
        case 'heif':
          mediaType = 'image/heif'
          break
        case 'vob':
          mediaType = 'video/vob' // Using video/vob based on common usage. Adjust if needed.
          break
        default:
          logger.warn(`Unknown extension '${fileExt}' for file with empty type: ${originalFilename}. Cannot determine mediaType.`)
          // Let the process continue to throw the error below if type is still missing.
          break
      }
    }

    // The media service requires a contentType. Throw an error if we couldn't determine one.
    if (!mediaType) {
      logger.error(`Failed to determine mediaType for ${originalFilename}. Upload aborted.`)
      createAlert(`Could not determine file type for ${originalFilename}. Upload failed.`)
      throw new Error(`Could not determine mediaType for ${originalFilename}`)
    }

    const isVideo = mediaType.toLowerCase().includes('video')
    const imageUrl = isVideo ? `${baseDomain}/${videoPlaceholder}` : URL.createObjectURL(file)
    const splitName = originalFilename.split('.')
    splitName.pop()
    const title = splitName.join('.')

    // Extract EXIF data for images
    const exifData = !isVideo ? await extractExif(file) : { dateTaken: new Date().toISOString() }

    // Start tracking progress
    const mediaData: MediaData = {
      albumId: albumId || undefined, // Ensure albumId is string or undefined
      title,
      mediaType,
      originalFilename,
      fileSize,
      imageUrl,
      createdAt: new Date().toISOString(),
      dateTaken: exifData.dateTaken,
      description: null,
      isBanned: false,
      isVideo,
      scheduledDeletionAt: null,
      userId,
      userTags: null,
      ...(exifData.width && exifData.height ? { width: exifData.width, height: exifData.height } : {})
    }
    beginTrackingProgress(bucketId, mediaId, mediaData)

    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('Not authenticated')

    try {
      const { data } = await axios.post<MediaServiceResponse>(
        `${config.api.mediaService}`,
        {
          bucketId,
          metadata: {
            dateTaken: exifData.dateTaken,
            contentType: mediaType,
            fileSize,
            ...(exifData.width ? { width: exifData.width } : {}),
            ...(exifData.height ? { height: exifData.height } : {}),
            originalFilename
          },
          albumId,
          title
        },
        {
          headers: {
            Authorization: `${token}`,
            'x-client-name': 'web',
            'x-client-version': process.env.REACT_APP_VERSION || '1.0.0',
            'x-request-id': mediaId
          }
        }
      )

      const { id, uploadUrls } = data
      const { chunkSize } = uploadUrls
      const numChunks = Math.ceil(fileSize / chunkSize)
      const parts: UploadPart[] = []
      let externalId: string | undefined

      // Use semaphore to limit concurrent chunk uploads
      const uploadPromises = Array.from({ length: numChunks }, (_, i) => {
        return chunkUploadSemaphore.execute(async () => {
          const start = i * chunkSize
          const end = Math.min(start + chunkSize, fileSize)
          const fileChunk = file.slice(start, end)

          try {
            const response = await axios.put(uploadUrls.urls[i], fileChunk)
            externalId = response.data.externalId // Assuming this is consistent across chunks? May need adjustment if not.

            // Important: Ensure parts are added in the correct order,
            // even though uploads might finish out of order.
            parts[i] = {
              // Use index 'i' to place the part correctly
              partNumber: i + 1,
              eTag: response.headers.etag
            }

            // Update progress based on completed chunks, not just bytes sent
            const completedChunks = parts.filter((p) => p).length // Count non-empty slots
            updateStatus(mediaId, Math.round((completedChunks / numChunks) * 100))
          } catch (error) {
            logger.error(`Failed to upload chunk ${i + 1}:`, error)
            // Ensure the overall upload fails if a chunk fails
            throw error // Re-throw to fail the Promise.all below
          }
        })
      })

      // Wait for all semaphore-controlled uploads to complete
      await Promise.all(uploadPromises)

      // Filter out any potential empty slots if an error occurred mid-way, though re-throwing should prevent this state.
      const finalParts = parts.filter((p) => p)
      if (finalParts.length !== numChunks) {
        throw new Error(`Upload incomplete: Expected ${numChunks} parts, got ${finalParts.length}`)
      }

      // Step 3: Complete the upload
      await axios.put(
        `${config.api.mediaService}/${id}`,
        { s3UploadParts: finalParts },
        {
          headers: {
            Authorization: `${token}`,
            'x-client-name': 'web',
            'x-client-version': process.env.REACT_APP_VERSION || '1.0.0',
            'x-request-id': externalId ?? mediaId
          }
        }
      )

      // Mark as 'PROCESSING' initially in the UI
      updateStatus(mediaId, 'PROCESSING')

      // Start polling for status changes
      pollMediaStatus(id)
    } catch (error) {
      logger.error('Upload failed:', error)
      if (axios.isAxiosError(error)) {
        logger.error('Response:', error.response?.data)
        logger.error('Request:', error.config)
      }
      throw error
    }

    // Update Apollo cache
    client.cache.updateFragment(
      {
        id: `Bucket:${bucketId}`,
        fragment: BucketFragmentFragmentDoc
      },
      (current) => {
        if (!current) return null

        const totalMedia = current?.counters?.totalMedia ?? 0
        const totalMediaMinusTrash = current.counters?.totalMediaMinusTrash ?? 0
        const totalSize = current?.counters?.totalSize ?? 0

        return {
          ...current,
          counters: {
            ...current.counters,
            totalMedia: totalMedia + 1,
            totalMediaMinusTrash: totalMediaMinusTrash + 1,
            totalSize: totalSize + fileSize
          }
        }
      }
    )

    client.cache.updateFragment(
      {
        id: `BucketAlbum:${albumId}`,
        fragment: BucketAlbumFragmentFragmentDoc
      },
      (current) => {
        if (!current) return null

        const totalMedia = current?.counters?.totalMedia ?? 0

        return {
          ...current,
          counters: {
            ...current.counters,
            totalMedia: totalMedia + 1
          }
        }
      }
    )

    return mediaId
  }

  const uploadSingle = async (file: File) => {
    try {
      trackEvent('media_upload_event', {
        metric: 'media_upload_event',
        id: userId,
        bucketId,
        albumId,
        isMultiple: false,
        platform: 'WEB'
      })

      return await uploadFile(file)
    } catch (error) {
      logger.error('Upload failed:', error)
      createAlert('Upload failed. Please try again.')
      throw error
    }
  }

  const uploadMultiple = async (files: File[]) => {
    // Store context locally as it might change if user navigates away during upload
    const currentBucketId = bucketId

    try {
      trackEvent('media_upload_event', {
        metric: 'media_upload_event',
        id: userId,
        bucketId,
        albumId,
        isMultiple: true,
        platform: 'WEB'
      })

      // Process files sequentially using reduce to satisfy linter rules against for...of with await
      const results = await files.reduce(async (accPromise, file) => {
        // Wait for the previous file upload promise to resolve
        const accumulatedResults = await accPromise

        try {
          // Await the current file upload
          const mediaId = await uploadFile(file)
          // Add the new result to the accumulated list
          return [...accumulatedResults, mediaId]
        } catch (error) {
          logger.error(`Failed to upload file ${file.name} in batch:`, error)
          createAlert(`Upload failed for ${file.name}. Continuing with others.`)
          // Return the results accumulated so far, excluding the failed one
          return accumulatedResults
        }
      }, Promise.resolve([] as string[])) // Initial value is a resolved promise with an empty array

      return results
    } catch (error) {
      logger.error('Batch upload failed:', error)
      createAlert('Upload failed. Please try again.')
      throw error // Re-throw batch-level errors
    } finally {
      // Refetch the main album query once all uploads in the batch are processed
      // This ensures thumbnails (especially for HEIC) are updated from the list query.
      if (currentBucketId) {
        // We always need a bucketId
        setTimeout(async () => {
          try {
            await client.refetchQueries({
              include: [BucketMediaByAlbumIdDocument]
            })
          } catch (refetchError) {
            logger.error(`Failed delayed refetch for bucket ${currentBucketId}:`, refetchError)
          }
        }, 2000) // Delay for 2 seconds (adjust as needed)
      }
    }
  }

  // Original implementation referenced from useBucketMediaUpload.tsx
  const { batchUpload } = useS3MulitpartUpload()
  const uploadChunk = async (chunks: File[][], num: number, total: number) => {
    const batch = chunks.pop()
    if (!batch) return
    await batchUpload(batch, num, total)
    await uploadChunk(chunks, num + 1, total)
  }

  const oldUploadMultiple = (files: File[]) => {
    const chunks = chunk(files, 5)
    return uploadChunk(chunks, 1, chunks.length)
  }

  return isEnabled ? { uploadSingle, uploadMultiple } : { uploadSingle: oldUploadSingle, uploadMultiple: oldUploadMultiple }
}

export default function useMediaServiceUpload() {
  const isEnabled = useFlag('useMediaAPIWeb')
  return useUploadImplementation(isEnabled)
}
